// ═══════════════════════════════════════════════════
//  ReconAI Pro v3 – Utility Functions
// ═══════════════════════════════════════════════════

let _idCounter = 0;
export const generateId = () => {
  try { return crypto.randomUUID(); }
  catch { return '_' + (++_idCounter) + '_' + Math.random().toString(36).substr(2, 12) + Date.now().toString(36); }
};

// Extract voucher number from nnn/YYYY-YYYY format (e.g., "76/2025-2026")
export const extractVoucherNumber = (str) => {
  if (!str) return '';
  const m = str.match(/(\d+)\/\d{4}-\d{4}/);
  return m ? m[1] : '';
};

// Extract cheque/check number from narration (CH#669172, CHQ NO 669172, etc.)
export const extractCheckNumber = (str) => {
  if (!str) return '';
  const patterns = [
    /CH#(\d{4,})/i,                    // CH#669172
    /CHQ?\s*(?:NO\.?\s*)?#?(\d{4,})/i, // CHQ NO 669172, CHQ#669172
    /CHEQUE\s*(?:NO\.?\s*)?#?(\d{4,})/i, // CHEQUE NO 669172
    /(?:NEFT|RTGS|UTR)\s*(?:NO\.?\s*)?#?(\w{6,})/i, // NEFT/RTGS refs
  ];
  for (const p of patterns) {
    const m = str.match(p);
    if (m) return m[1];
  }
  return '';
};

// Extract all invoice references from a BPN narration line
// "CH#669172 Inv#2422533 Dated on 2025-01-30 Bill Amt 61..." → "2422533"
export const extractInvFromNarration = (narr) => {
  if (!narr) return [];
  const refs = [];
  const patterns = [
    /Inv#(\d[\d/-]+)/gi,               // Inv#2422533, Inv#76/2025-2026
    /Invoice\s*#?\s*(\d[\d/-]+)/gi,    // Invoice #2422533
    /Bill\s*no\.?\s*#?\s*([\w/-]+)/gi, // Bill no # PR25/955, Bill No. 123860
    /Bill\s+(\d{4,})/gi,              // Bill 123860 (standalone)
  ];
  for (const p of patterns) {
    let m;
    while ((m = p.exec(narr)) !== null) {
      refs.push(m[1].trim());
    }
  }
  return refs;
};

export const formatCurrency = (amount) => {
  if (amount == null || isNaN(amount)) return '₹0.00';
  const num = Math.abs(Number(amount));
  const sign = Number(amount) < 0 ? '-' : '';
  const formatted = num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${sign}₹${formatted}`;
};

export const formatDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d)) return date;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

export const parseAmount = (str) => {
  if (!str) return 0;
  str = String(str).trim().replace(/[₹$€£¥,\s]/g, '');
  str = str.replace(/\((.+)\)/, '-$1');
  const num = parseFloat(str);
  return isNaN(num) ? 0 : Math.round(num * 100) / 100;
};

export const parseDateToISO = (dateStr) => {
  if (!dateStr) return '';
  let m;
  // DD-MM-YYYY or DD/MM/YYYY
  m = dateStr.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/);
  if (m) return `${m[3]}-${m[2].padStart(2,'0')}-${m[1].padStart(2,'0')}`;
  // YYYY-MM-DD
  m = dateStr.match(/^(\d{4})[-/.](\d{2})[-/.](\d{2})$/);
  if (m) return dateStr.replace(/\//g, '-');
  // D-Mon-YY or D-Mon-YYYY
  const months = {jan:'01',feb:'02',mar:'03',apr:'04',may:'05',jun:'06',jul:'07',aug:'08',sep:'09',oct:'10',nov:'11',dec:'12'};
  m = dateStr.match(/^(\d{1,2})[-/.]([A-Za-z]{3,9})[-/.](\d{2,4})$/);
  if (m) {
    const day = m[1].padStart(2, '0');
    const mon = months[m[2].toLowerCase().substring(0, 3)];
    let year = m[3];
    if (year.length === 2) year = (parseInt(year) > 50 ? '19' : '20') + year;
    if (mon) return `${year}-${mon}-${day}`;
  }
  // MM/DD/YYYY (US format — only if month <= 12 and day > 12 to disambiguate)
  m = dateStr.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/);
  if (m) {
    const a = parseInt(m[1]), b = parseInt(m[2]);
    // Already handled as DD-MM-YYYY above; handle MM-DD-YYYY only when first part > 12 isn't possible
    // but if first part <= 12 and second > 12, it's MM/DD/YYYY
    if (a <= 12 && b > 12) {
      return `${m[3]}-${m[1].padStart(2,'0')}-${m[2].padStart(2,'0')}`;
    }
  }
  // DD-Month-YYYY with full month name (e.g., "15-March-2025")
  const fullMonths = {january:'01',february:'02',march:'03',april:'04',may:'05',june:'06',july:'07',august:'08',september:'09',october:'10',november:'11',december:'12'};
  m = dateStr.match(/^(\d{1,2})[-/.]([A-Za-z]+)[-/.](\d{4})$/);
  if (m) {
    const day = m[1].padStart(2, '0');
    const mon = fullMonths[m[2].toLowerCase()] || months[m[2].toLowerCase().substring(0, 3)];
    if (mon) return `${m[3]}-${mon}-${day}`;
  }
  return '';
};

export const daysBetween = (d1, d2) => {
  const a = new Date(d1), b = new Date(d2);
  if (isNaN(a) || isNaN(b)) return 999;
  return Math.abs(Math.floor((a - b) / (1000 * 60 * 60 * 24)));
};

export const normalizeDocNo = (docNo) => {
  if (!docNo) return '';
  return docNo.toUpperCase().replace(/\s+/g, '').replace(/^(PI|JW|BPN|PR2|ESV|CH|BR|JV|CN|DN|SI|INV)\d{2}\//i, '');
};

export const normalizeNarration = (narr) => {
  if (!narr) return '';
  return narr.toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\b(the|and|of|for|in|on|to|from|by|at|is|was|with|as|this|that|an|a)\b/g, '')
    .replace(/\s+/g, ' ')
    .trim();
};

export const stringSimilarity = (a, b) => {
  if (!a || !b) return 0;
  a = a.toLowerCase(); b = b.toLowerCase();
  if (a === b) return 1;
  const setA = new Set(a.split(/\s+/));
  const setB = new Set(b.split(/\s+/));
  const intersection = new Set([...setA].filter(x => setB.has(x)));
  const union = new Set([...setA, ...setB]);
  return union.size === 0 ? 0 : intersection.size / union.size;
};

export const levenshtein = (a, b) => {
  if (!a || !b) return Math.max((a||'').length, (b||'').length);
  const matrix = [];
  for (let i = 0; i <= b.length; i++) matrix[i] = [i];
  for (let j = 0; j <= a.length; j++) matrix[0][j] = j;
  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      matrix[i][j] = b[i-1] === a[j-1]
        ? matrix[i-1][j-1]
        : Math.min(matrix[i-1][j-1]+1, matrix[i][j-1]+1, matrix[i-1][j]+1);
    }
  }
  return matrix[b.length][a.length];
};

export const docNoSimilarity = (a, b) => {
  if (!a || !b) return 0;
  const na = normalizeDocNo(a), nb = normalizeDocNo(b);
  if (na === nb) return 1;
  if (a.toUpperCase() === b.toUpperCase()) return 1;
  // Check if one contains the other
  const ua = a.toUpperCase(), ub = b.toUpperCase();
  if (ua.includes(ub) || ub.includes(ua)) return 0.85;
  // Levenshtein on normalized
  const maxLen = Math.max(na.length, nb.length);
  if (maxLen === 0) return 0;
  return 1 - levenshtein(na, nb) / maxLen;
};

export const getMatchStatusColor = (status) => {
  switch (status) {
    case 'CONFIRMED': return 'success';
    case 'SUGGESTED': return 'warning';
    case 'PARTIAL': return 'purple';
    case 'REJECTED': return 'danger';
    default: return 'neutral';
  }
};

export const getExceptionColor = (cat) => {
  switch (cat) {
    case 'Missing in Company': return 'danger';
    case 'Missing in Supplier': return 'danger';
    case 'Amount Mismatch': return 'warning';
    case 'Timing Difference': return 'info';
    case 'Probable Duplicate': return 'purple';
    default: return 'neutral';
  }
};

export const downloadJSON = (data, filename) => {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

export const downloadCSV = (rows, headers, filename) => {
  const csvContent = [
    headers.join(','),
    ...rows.map(row => headers.map(h => `"${String(row[h] || '').replace(/"/g, '""')}"`).join(','))
  ].join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};
